import { cx } from "@/lib/cx";

import styles from "./Avatar.module.css";

/** Number of collaborator colours in the tokens (--color-person-1 … 6). */
export const PERSON_COLOURS = 6;

export interface AvatarPerson {
  name: string;
  initials: string;
  /** 1-based collaborator colour. */
  colour: number;
}

export interface AvatarProps {
  person: AvatarPerson;
  size?: "sm" | "md";
  className?: string | undefined;
}

export function Avatar({ person, size = "md", className }: AvatarProps) {
  const colour = ((person.colour - 1) % PERSON_COLOURS) + 1;
  return (
    <span
      className={cx(styles.avatar, styles[size], styles[`person${String(colour)}`], className)}
      title={person.name}
    >
      <span aria-hidden="true">{person.initials}</span>
      <span className="sr-only">{person.name}</span>
    </span>
  );
}

export interface AvatarStackProps {
  people: readonly AvatarPerson[];
  /** Show at most this many; the rest become "+n". */
  max: number;
  size?: "sm" | "md";
  className?: string | undefined;
}

export function AvatarStack({ people, max, size = "sm", className }: AvatarStackProps) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span className={cx(styles.stack, className)}>
      {shown.map((person) => (
        <Avatar key={person.name} person={person} size={size} className={styles.stacked} />
      ))}
      {rest > 0 && (
        <span className={cx(styles.avatar, styles[size], styles.more, styles.stacked)}>
          <span aria-hidden="true">+{rest}</span>
          <span className="sr-only">and {rest} more</span>
        </span>
      )}
    </span>
  );
}
